import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { findSponsorsByName } from './utils';

import type { Sponsor } from '@shared-types/sponsor.types';
import { getSponsorDataFromService } from '@utilities/sponsor';

export const useSponsors = ({
  sponsorName,
  competitionId = window.competitionId,
  extract,
  enable = true,
  useMainSponsors = false,
  tagsToSplit = [],
  enableLogo = false,
  mainTitleTag,
}: {
  sponsorName?: string;
  competitionId?: string;
  extract?: number;
  enable?: boolean;
  useMainSponsors?: boolean;
  tagsToSplit?: string[];
  enableLogo?: boolean;
  mainTitleTag?: string;
}) => {
  const { data: sponsors, isFetched } = useQuery({
    queryKey: ['sponsors', competitionId, useMainSponsors],
    queryFn: ({ signal }) => getSponsorDataFromService(competitionId, useMainSponsors, signal),
    enabled: !!enable,
    placeholderData: [],
  });

  const loaded = isFetched || !enable;

  const extractedSponsors = useMemo(() => {
    if (!enable) return [];

    if (sponsorName) {
      //here we are in sponsored or article page
      const singleSponsor = findSponsorsByName(sponsors, sponsorName);
      return singleSponsor ? [singleSponsor] : [];
    }

    if (!extract) {
      //here we are in the footer case
      return sponsors ?? [];
    }

    //here we are in random sponsor in upper right corner case
    //needed for immutability of the array in cache
    return [...sponsors]
      .filter((s) => s.type === 'GLOBAL' || s.type === 'LOCAL')
      .toSorted(() => 0.5 - Math.random())
      .slice(0, extract);
  }, [enable, sponsorName, extract, sponsors]);

  const globalSponsors = useMemo(() => extractedSponsors.filter((s) => s.type === 'GLOBAL'), [extractedSponsors]);
  const othersSponsors = useMemo(() => extractedSponsors.filter((s) => s.type === 'OTHER'), [extractedSponsors]);
  const localSponsors = useMemo(() => extractedSponsors.filter((s) => s.type === 'LOCAL'), [extractedSponsors]);
  const licenseeSponsors = useMemo(() => extractedSponsors.filter((s) => s.type === 'LICENSEE'), [extractedSponsors]);
  const taggedSponsors = useMemo(
    () =>
      tagsToSplit?.reduce<Record<string, Sponsor[]>>((acc, tag) => {
        const sponsors = licenseeSponsors.filter((s) => s.tags.includes(tag));
        return sponsors.length ? { ...acc, [tag]: sponsors } : acc;
      }, {}),
    [licenseeSponsors, tagsToSplit],
  );

  return {
    sponsors: extractedSponsors,
    globalSponsors,
    localSponsors,
    othersSponsors,
    taggedSponsors,
    loaded,
    competitionId,
    enableLogo,
    mainTitleTag,
  };
};
